import { apiService, type PagingApiAndNotifierConfig } from "@modernary/mui/utils/api";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import { NotifyOptionsSuccess, NotifyOptionsError } from "@modernary/mui/components/notifications";
import { apiHosts } from "@/api/constants/apiHosts";
import type {
  ComplaintInvestigation,
  ComplaintInvestigationNew,
  ComplaintInvestigationQuery,
} from "../inspections/inspection";
import complaintInvestigationService from "./complaintInvestigationService";
import type { AxiosResponse } from "axios";
import { LE } from "@/constants/enums";

const COMP_INV_URL = "inspections/complaints"; //Dedicated endpoint specifically for complaint investigations
const URL = "inspections"; //Shared endpoint for the rest of investigation/inspection endpoints
const ENTITY_NAME = "Complaint Investigation";

const axiosParams = {
  // Set different base URL based on the environment
  baseURL: import.meta.env.VITE_AQMS_API_BASE_URL,
};
const api = apiService<ComplaintInvestigation>(apiHosts.AQMS, axiosParams);

const getComplaintInvestigations = (params: ComplaintInvestigationQuery, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  const url = config.pagingUrl ? config.pagingUrl : COMP_INV_URL;

  return api.getPaged(url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successNotifCb,
    errorNotifCb,
  });
};

const getComplaintInvestigationById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.get(URL + "/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

const getNewComplaintInvestigation = async (ids: ComplaintInvestigationIds) => {
  let dustControlInspection = {
    isRain: false,
    isPcfSubmitted: false,
    hasTrackout: false,
    hasValidDustPermit: true,
  };
  const defaultApp = {
    data: {
      ...complaintInvestigationService.getInitialData(),
      ...ids,
      vacantLandInspection: {},
      dustControlInspection: dustControlInspection,
      categoryMurn: LE.InspectionCategory.Investigation,
    },
  } as AxiosResponse<ComplaintInvestigationNew, any>;

  return defaultApp;
};

type ComplaintInvestigationIds = {
  id?: string | number;
};
const getComplaintInvestigationByIdOrNew = (
  ids: ComplaintInvestigationIds,
  config: PagingApiAndNotifierConfig = {},
) => {
  if (!ids.id) {
    return getNewComplaintInvestigation(ids);
  } else {
    return getComplaintInvestigationById(ids.id, config) as Promise<AxiosResponse<ComplaintInvestigationNew, any>>;
  }
};

const createComplaintInvestigation = (inspection: ComplaintInvestigation, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.created = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.created = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.post(URL, inspection, {
    successNotifCb,
    errorNotifCb,
  });
};

const updateComplaintInvestigation = (inspection: ComplaintInvestigation, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.updated = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.put(URL + "/" + inspection.id, inspection, {
    successNotifCb,
    errorNotifCb,
  });
};

/**
 * @param {ComplaintInvestigation} inspection This cannot be a 'ref' to a inspection
 */
const saveComplaintInvestigation = (inspection: ComplaintInvestigation, config = {}) => {
  if (inspection.id) {
    return updateComplaintInvestigation(inspection, config);
  } else {
    return createComplaintInvestigation(inspection, config);
  }
};

const deleteComplaintInvestigationById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.deleted = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.deleted = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.delete(URL + "/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

export {
  deleteComplaintInvestigationById,
  getComplaintInvestigations,
  getComplaintInvestigationById,
  getNewComplaintInvestigation,
  getComplaintInvestigationByIdOrNew,
  createComplaintInvestigation,
  updateComplaintInvestigation,
  saveComplaintInvestigation,
};
